<script setup>
/* global axios */
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();

const props = defineProps({
  funnelId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['integration-changed']);

// State
const isLoading = ref(true);
const isSyncing = ref(false);
const isDisconnecting = ref(false);
const integrationStatus = ref(null);
const availableCalendars = ref([]);
const showSettings = ref(false);
const syncStages = ref([]);
const selectedCalendarId = ref('primary');
const addContactsAsAttendees = ref(false);
const eventDuration = ref(60);

// Computed
const currentAccountId = computed(() => store.getters['getCurrentAccountId']);
const isConnected = computed(() => integrationStatus.value?.connected || false);
const isConfigured = computed(() => integrationStatus.value?.configured || false);
const isEnabled = computed(() => integrationStatus.value?.enabled || false);
const integration = computed(() => integrationStatus.value?.integration);
const googleEmail = computed(() => integration.value?.google_email || '');
const lastSyncAt = computed(() => {
  const dt = integration.value?.last_sync_at;
  if (!dt) return null;
  return new Date(dt).toLocaleString('pt-BR');
});

const selectedFunnel = computed(() => store.getters['funnel/getSelectedFunnel']);
const funnelStages = computed(() => {
  if (!selectedFunnel.value?.stages) return [];
  return Object.entries(selectedFunnel.value.stages)
    .map(([id, data]) => ({ id, name: data.name, color: data.color, position: data.position }))
    .sort((a, b) => a.position - b.position);
});

// API helpers — uses global axios which has auth interceptors configured
const apiBase = computed(
  () => `/api/v1/accounts/${currentAccountId.value}/google_calendar`
);

const apiCall = async (method, path, data = null) => {
  const url = `${apiBase.value}${path}`;
  const response = await axios({ method, url, data });
  return response.data;
};

// Fetch integration status
const fetchStatus = async () => {
  try {
    isLoading.value = true;
    const data = await apiCall('GET', `/status?funnel_id=${props.funnelId}`);
    integrationStatus.value = data;

    if (data.integration?.settings) {
      syncStages.value = data.integration.settings.sync_stages || [];
      eventDuration.value = data.integration.settings.event_duration_minutes || 60;
    }
    if (data.integration) {
      selectedCalendarId.value = data.integration.google_calendar_id || 'primary';
      addContactsAsAttendees.value = data.integration.add_contacts_as_attendees || false;
    }
  } catch (error) {
    console.error('[GoogleCalendarConnect] Failed to fetch status:', error);
  } finally {
    isLoading.value = false;
  }
};

// Connect to Google Calendar
const handleConnect = async () => {
  try {
    const data = await apiCall('GET', `/authorize?funnel_id=${props.funnelId}`);
    if (data.authorization_url) {
      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      window.open(
        data.authorization_url,
        'google_calendar_auth',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
      );

      // Listen for postMessage from the callback page (avoids COOP issues)
      const messageHandler = (event) => {
        if (event.data?.type === 'google_calendar_connected') {
          window.removeEventListener('message', messageHandler);
          fetchStatus();
          emit('integration-changed');
          if (event.data.success) {
            emitter.emit('newToastMessage', {
              message: 'Google Calendar conectado com sucesso!',
              action: { type: 'success' },
            });
          }
        }
      };
      window.addEventListener('message', messageHandler);
    }
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: `Erro ao conectar: ${error.message}`,
      action: { type: 'error' },
    });
  }
};

// Disconnect
const handleDisconnect = async () => {
  try {
    isDisconnecting.value = true;
    await apiCall('DELETE', `/disconnect?funnel_id=${props.funnelId}`);
    integrationStatus.value = { ...integrationStatus.value, connected: false, integration: null };
    emitter.emit('newToastMessage', {
      message: 'Google Calendar desconectado com sucesso',
      action: { type: 'success' },
    });
    emit('integration-changed');
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: `Erro ao desconectar: ${error.message}`,
      action: { type: 'error' },
    });
  } finally {
    isDisconnecting.value = false;
  }
};

// Force sync
const handleSync = async () => {
  try {
    isSyncing.value = true;
    await apiCall('POST', `/sync?funnel_id=${props.funnelId}`);
    emitter.emit('newToastMessage', {
      message: 'Sincronização iniciada em segundo plano',
      action: { type: 'success' },
    });
    // Refresh status after a delay
    setTimeout(() => fetchStatus(), 3000);
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: `Erro ao sincronizar: ${error.message}`,
      action: { type: 'error' },
    });
  } finally {
    isSyncing.value = false;
  }
};

// Save settings
const handleSaveSettings = async () => {
  try {
    await apiCall('PATCH', `/settings?funnel_id=${props.funnelId}`, {
      add_contacts_as_attendees: addContactsAsAttendees.value,
      google_calendar_id: selectedCalendarId.value,
      settings: {
        sync_stages: syncStages.value.length > 0 ? syncStages.value : null,
        event_duration_minutes: eventDuration.value,
      },
    });
    emitter.emit('newToastMessage', {
      message: 'Configurações salvas com sucesso',
      action: { type: 'success' },
    });
    emit('integration-changed');
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: `Erro ao salvar: ${error.message}`,
      action: { type: 'error' },
    });
  }
};

// Fetch available calendars
const fetchCalendars = async () => {
  try {
    const data = await apiCall('GET', `/calendars?funnel_id=${props.funnelId}`);
    availableCalendars.value = data.calendars || [];
  } catch (error) {
    console.error('[GoogleCalendarConnect] Failed to fetch calendars:', error);
  }
};

// Toggle stage sync
const toggleStageSync = (stageId) => {
  const index = syncStages.value.indexOf(stageId);
  if (index >= 0) {
    syncStages.value.splice(index, 1);
  } else {
    syncStages.value.push(stageId);
  }
};

const isStageSelected = (stageId) => {
  return syncStages.value.length === 0 || syncStages.value.includes(stageId);
};

// Lifecycle
onMounted(async () => {
  await fetchStatus();
});

watch(() => props.funnelId, async () => {
  await fetchStatus();
});
</script>

<template>
  <div class="google-calendar-connect">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <div class="w-6 h-6 border-2 border-woot-500 border-t-transparent rounded-full animate-spin" />
    </div>

    <!-- Not Configured (SuperAdmin hasn't set credentials) -->
    <div v-else-if="!isConfigured" class="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
      <div class="flex items-start gap-3">
        <div class="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
          <span class="text-xl">⚠️</span>
        </div>
        <div>
          <h4 class="text-sm font-semibold text-amber-800 dark:text-amber-200">Não configurado</h4>
          <p class="text-xs text-amber-700 dark:text-amber-300 mt-1">
            A integração com Google Calendar precisa ser configurada pelo administrador do sistema.
          </p>
        </div>
      </div>
    </div>

    <!-- Disconnected State -->
    <div v-else-if="!isConnected" class="space-y-4">
      <div class="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-white dark:bg-slate-800 shadow-sm flex items-center justify-center flex-shrink-0">
            <svg class="w-7 h-7" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </div>
          <div class="flex-1">
            <h4 class="text-sm font-semibold text-slate-800 dark:text-slate-200">
              Google Calendar
            </h4>
            <p class="text-xs text-slate-600 dark:text-slate-400 mt-1">
              Conecte sua conta Google para sincronizar agendamentos deste funil com seu calendário pessoal.
            </p>
          </div>
        </div>

        <button
          type="button"
          class="mt-4 w-full px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
          @click="handleConnect"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
          Conectar Google Calendar
        </button>
      </div>
    </div>

    <!-- Connected State -->
    <div v-else class="space-y-4">
      <!-- Connection Info -->
      <div class="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
            <span class="text-lg">✅</span>
          </div>
          <div class="flex-1 min-w-0">
            <h4 class="text-sm font-semibold text-emerald-800 dark:text-emerald-200">Conectado</h4>
            <p class="text-xs text-emerald-700 dark:text-emerald-300 truncate">
              {{ googleEmail }}
            </p>
            <p v-if="lastSyncAt" class="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
              Última sync: {{ lastSyncAt }}
            </p>
          </div>
          <div class="flex items-center gap-1.5 flex-shrink-0">
            <button
              type="button"
              class="p-2 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 rounded-lg transition-colors"
              :disabled="isSyncing"
              title="Sincronizar agora"
              @click="handleSync"
            >
              <svg
                class="w-4 h-4"
                :class="{ 'animate-spin': isSyncing }"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M21 12a9 9 0 11-6.219-8.56"/>
                <polyline points="21 3 21 9 15 9"/>
              </svg>
            </button>
            <button
              type="button"
              class="p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              title="Configurações"
              @click="showSettings = !showSettings"
            >
              <fluent-icon icon="settings" size="16" />
            </button>
          </div>
        </div>
      </div>

      <!-- Settings Panel -->
      <div v-if="showSettings" class="p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl space-y-4 animate-fade-in">
        <h4 class="text-sm font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
          <fluent-icon icon="settings" size="16" />
          Configurações
        </h4>

        <!-- Calendar selector -->
        <div class="space-y-1.5" v-if="availableCalendars.length > 0">
          <label class="text-xs font-medium text-slate-600 dark:text-slate-400">Calendário</label>
          <select
            v-model="selectedCalendarId"
            class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
          >
            <option v-for="cal in availableCalendars" :key="cal.id" :value="cal.id">
              {{ cal.summary }} {{ cal.primary ? '(Principal)' : '' }}
            </option>
          </select>
        </div>

        <!-- Contacts as attendees toggle -->
        <div class="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
          <input
            id="gcal-attendees"
            v-model="addContactsAsAttendees"
            type="checkbox"
            class="w-4 h-4 text-woot-500 border-slate-300 rounded focus:ring-woot-500"
          />
          <div>
            <label for="gcal-attendees" class="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
              Adicionar contato como participante
            </label>
            <p class="text-xs text-slate-500 dark:text-slate-500">
              O contato receberá um convite de calendário por email
            </p>
          </div>
        </div>

        <!-- Event duration -->
        <div class="space-y-1.5">
          <label class="text-xs font-medium text-slate-600 dark:text-slate-400">Duração padrão do evento</label>
          <select
            v-model="eventDuration"
            class="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
          >
            <option :value="15">15 minutos</option>
            <option :value="30">30 minutos</option>
            <option :value="45">45 minutos</option>
            <option :value="60">1 hora</option>
            <option :value="90">1h 30min</option>
            <option :value="120">2 horas</option>
          </select>
        </div>

        <!-- Stage filter -->
        <div class="space-y-2">
          <label class="text-xs font-medium text-slate-600 dark:text-slate-400">
            Sincronizar etapas
            <span class="text-slate-400 font-normal">(vazio = todas)</span>
          </label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="stage in funnelStages"
              :key="stage.id"
              type="button"
              class="px-3 py-1.5 text-xs font-medium rounded-full border transition-all duration-150"
              :class="isStageSelected(stage.id) && syncStages.length > 0
                ? 'bg-woot-50 dark:bg-woot-900/20 border-woot-300 dark:border-woot-700 text-woot-700 dark:text-woot-300'
                : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-300'"
              @click="toggleStageSync(stage.id)"
            >
              <span
                class="inline-block w-2 h-2 rounded-full mr-1.5"
                :style="{ backgroundColor: stage.color || '#64748B' }"
              />
              {{ stage.name }}
            </button>
          </div>
        </div>

        <!-- Save / Disconnect buttons -->
        <div class="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            class="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg transition-colors"
            :disabled="isDisconnecting"
            @click="handleDisconnect"
          >
            {{ isDisconnecting ? 'Desconectando...' : '❌ Desconectar' }}
          </button>
          <button
            type="button"
            class="px-4 py-1.5 text-xs font-medium text-white bg-woot-600 hover:bg-woot-700 rounded-lg transition-colors"
            @click="handleSaveSettings"
          >
            Salvar Configurações
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
